import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Checkbox,
  FormControlLabel,
  TextField,
  Card,
  CardContent,
  Avatar,
  Chip,
  InputAdornment,
  Fade,
  Grow,
  Stack,
  Divider,
  Collapse,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Send as SendIcon,
  FileDownload as FileDownloadIcon,
  ArrowBack as ArrowBackIcon,
  WhatsApp as WhatsAppIcon,
  MailOutline as MailOutlineIcon,
  Search as SearchIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  LocationOn as LocationOnIcon,
  CreditCard as CreditCardIcon,
  AddCircleOutline,
  CloudSync,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TextSnippet as TextSnippetIcon
} from "@mui/icons-material";
import ResponsiveLayout from "./ResponsiveLayout";
import { useNavigate } from "react-router-dom";
import * as Sentry from "@sentry/react";
import axios from "axios";
import ManualCardModal from "./ManualCardModal";
import TextToCardModal from "./TextToCardModal";

// Debounce utility to avoid search lag
function useDebouncedValue(value, delay = 200) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

const CardHistoryPage = ({ onLogout, currentUser, companyData }) => {
  const navigate = useNavigate();
  const mountedRef = useRef(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [isManualModalOpen, setIsManualModalOpen] = useState(false);
  const [cards, setCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sendingEmailTo, setSendingEmailTo] = useState(null);
  const [selectedCardIndices, setSelectedCardIndices] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteInputs, setNoteInputs] = useState({});
  const [savingNoteId, setSavingNoteId] = useState(null);
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [isTextModalOpen, setIsTextModalOpen] = useState(false);

  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendType, setSendType] = useState("");
  const [sendMethod, setSendMethod] = useState("dynamic");
  const [selectedCardForSend, setSelectedCardForSend] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false);

  const API_BASE = import.meta.env.VITE_API_BASE;
  const debouncedQuery = useDebouncedValue(searchQuery);

  /**
   * Fetches visiting cards from the API.
   * @param {boolean} isSilent - If true, doesn't trigger the full-page loading spinner.
   */
  const fetchCards = useCallback(
    async (isSilent = false) => {
      if (!isSilent) setLoading(true);
      try {
        // THIS IS THE CORRECTED LINE: Pointing to the new PHP file with team logic.
        const response = await axios.post(
          `${API_BASE}/fetch-visiting-cards`,
          {},
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          }
        );

        if (mountedRef.current) {
          if (response.data.success) {
            const sortedCards = [...response.data.cards].sort((a, b) => {
              if (!a.created_at || !b.created_at) return 0;
              return new Date(b.created_at) - new Date(a.created_at);
            });
            setCards(sortedCards);
            setFilteredCards(sortedCards);
            setError("");
          } else {
            setError(response.data.message || "No history available.");
            setCards([]); // Clear cards on failure
            setFilteredCards([]);
          }
        }
      } catch (err) {
        if (mountedRef.current) {
          const msg =
            err.response?.data?.message || "Failed to fetch card history.";
          setError(msg);
          if (err.response && err.response.status >= 500) {
            Sentry.captureException(err, {
              tags: { section: "cards-history" },
            });
          }
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    },
    [API_BASE]
  );

  const handleDeleteSelected = async () => {
    if (selectedCardIndices.length === 0) return;

    const cardIdsToDelete = selectedCardIndices.map(
      (index) => filteredCards[index].id
    );

    if (
      !window.confirm(
        `Are you sure you want to delete ${cardIdsToDelete.length} selected cards?`
      )
    ) {
      return;
    }

    setIsDeleting(true);
    try {
      const response = await axios.post(
        `${API_BASE}/delete-visiting-cards`,
        { card_ids: cardIdsToDelete },
        { withCredentials: true }
      );

      if (response.data.success) {
        setSelectedCardIndices([]);
        setSelectAll(false);
        fetchCards(true);
      } else {
        alert(response.data.message || "Failed to delete cards.");
      }
    } catch (err) {
      console.error("Delete error:", err);
      alert("An error occurred while deleting cards.");
    } finally {
      setIsDeleting(true); // Wait, I should set it to false
      setIsDeleting(false);
    }
  };

  const fetchTemplates = useCallback(async () => {
    setIsFetchingTemplates(true);
    try {
      const response = await axios.post(`${API_BASE}/get-templates`, {}, { withCredentials: true });
      if (response.data.success) {
        setTemplates(response.data.templates);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
    } finally {
      setIsFetchingTemplates(false);
    }
  }, [API_BASE]);

  // Initial Load
  useEffect(() => {
    mountedRef.current = true;
    fetchCards();
    fetchTemplates();
    return () => {
      mountedRef.current = false;
    };
  }, [fetchCards, fetchTemplates]);

  // Debounced search logic
  useEffect(() => {
    if (!debouncedQuery.trim()) {
      setFilteredCards(cards);
      setSelectedCardIndices([]);
      setSelectAll(false);
      return;
    }
    const query = debouncedQuery.toLowerCase();
    setFilteredCards(
      cards.filter(
        (card) =>
          card.name?.toLowerCase().includes(query) ||
          card.company?.toLowerCase().includes(query) ||
          card.email?.toLowerCase().includes(query) ||
          card.phone?.toLowerCase().includes(query)
      )
    );
    setSelectedCardIndices([]);
    setSelectAll(false);
  }, [debouncedQuery, cards]);

  const sendEmailToContact = async (contact) => {
    if (!contact?.email || !contact.subject || !contact.body) {
      alert("Missing email data.");
      return;
    }
    setSendingEmailTo(contact.email);
    const formData = new FormData();
    formData.append("to", contact.email);
    formData.append("subject", contact.subject);
    formData.append("body", contact.body);

    try {
      const res = await axios.post(`${API_BASE}/send-email`, formData, {
        withCredentials: true,
      });
      alert(
        res.data.success
          ? `✅ Email sent to ${contact.name}`
          : `❌ Failed: ${res.data.error}`
      );
    } catch (err) {
      alert("❌ Error sending email.");
    } finally {
      setSendingEmailTo(null);
    }
  };
  const syncToLeadSeed = async () => {
    if (!selectedCardIndices.length)
      return alert("Select at least one card to sync.");

    setIsSyncing(true);
    try {
      const cardsToSync = selectedCardIndices.map((i) => filteredCards[i]);

      const res = await axios.post(
        `${API_BASE}/create-leadseed-sync`,
        {
          cards: cardsToSync,
        },
        {
          withCredentials: true,
        }
      );

      if (res.data.success && res.data.syncId) {
        const LEADSEED_FRONTEND = import.meta.env.VITE_LEADSEED_URL;
        window.location.href = `${LEADSEED_FRONTEND}/sync?id=${res.data.syncId}`;
      } else {
        alert("Failed to initiate sync.");
      }
    } catch (err) {
      alert("Failed to initiate sync with LeadSeed.");
      console.error(err);
    } finally {
      setIsSyncing(false);
    }
  };

  const toggleNotes = (cardId) => {
    setExpandedNotes(prev => ({ ...prev, [cardId]: !prev[cardId] }));
  };

  const handleAddNote = (cardId) => {
    setEditingNoteId(`add_${cardId}`);
    setNoteInputs({ ...noteInputs, [`add_${cardId}`]: "" });
    setExpandedNotes(prev => ({ ...prev, [cardId]: true }));
  };

  const handleEditNote = (note) => {
    setEditingNoteId(note.id);
    setNoteInputs({ ...noteInputs, [note.id]: note.note_text });
  };

  const cancelEditNote = () => {
    setEditingNoteId(null);
  };

  const saveNote = async (cardId, noteId = null) => {
    const isEditing = !!noteId;
    const stateKey = isEditing ? noteId : `add_${cardId}`;
    setSavingNoteId(stateKey);
    try {
      const textToSave = noteInputs[stateKey] || "";
      if (!textToSave.trim() && !isEditing) return alert("Note cannot be empty");

      const payload = { visiting_card_id: cardId, note_text: textToSave };
      if (isEditing) payload.note_id = noteId;

      const res = await axios.post(`${API_BASE}/save-card-note`, payload, { withCredentials: true });

      if (res.data.success) {
        const updateNotesArray = (notes) => {
          let currentNotes = notes ? [...notes] : [];
          if (isEditing) {
            return currentNotes.map(n => n.id === noteId ? { ...n, note_text: textToSave, updated_at: new Date().toISOString() } : n);
          } else {
            currentNotes.push({
              id: res.data.note_id,
              note_text: textToSave,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
            return currentNotes;
          }
        };

        setCards(cards.map(c => c.id === cardId ? { ...c, notes: updateNotesArray(c.notes) } : c));
        setFilteredCards(filteredCards.map(c => c.id === cardId ? { ...c, notes: updateNotesArray(c.notes) } : c));
        setEditingNoteId(null);
      } else {
        alert(res.data.message || "Failed to save note");
      }
    } catch (err) {
      console.error(err);
      alert("Error saving note");
    } finally {
      setSavingNoteId(null);
    }
  };

  const handleOpenSendModal = (card, type) => {
    setSelectedCardForSend(card);
    setSendType(type);
    setSendModalOpen(true);
    setSendMethod("dynamic");
    setSelectedTemplateId("");
  };

  const handleConfirmSend = () => {
    if (!selectedCardForSend) return;

    if (sendMethod === "dynamic") {
      if (sendType === "email") {
        window.location.href = selectedCardForSend.mailto;
      } else {
        window.location.href = selectedCardForSend.whatsapp;
      }
      setSendModalOpen(false);
    } else {
      const template = templates.find((t) => t.id === selectedTemplateId);
      if (!template) {
        alert("Please select a template.");
        return;
      }

      const filledBody = fillPlaceholders(template.body, selectedCardForSend, currentUser);
      console.log(filledBody)

      if (sendType === "email") {
        const subject = encodeURIComponent(template.title);
        const body = encodeURIComponent(filledBody);
        window.location.href = `mailto:${selectedCardForSend.email}?subject=${subject}&body=${body}`;
      } else {
        const phone = selectedCardForSend.phone?.replace(/[^0-9]/g, "");
        const body = encodeURIComponent(filledBody);
        window.open(`https://wa.me/${phone}?text=${body}`, "_blank");
      }
      setSendModalOpen(false);
    }
  };

  const fillPlaceholders = (text, card, user, companyData) => {
    let filled = text;
    // Client placeholders
    filled = filled.replace(/{{client_name}}/g, card.name || "");
    filled = filled.replace(/{{client_company}}/g, card.company || "");
    filled = filled.replace(/{{client_title}}/g, card.title || "");
    filled = filled.replace(/{{client_email}}/g, card.email || "");
    filled = filled.replace(/{{client_phone}}/g, card.phone || "");
    filled = filled.replace(/{{client_website}}/g, card.website || "");

    // User placeholders
    filled = filled.replace(/{{name}}/g, user.name || "");
    filled = filled.replace(/{{company}}/g, companyData?.companyName || "");
    filled = filled.replace(/{{role}}/g, companyData?.role || "");
    filled = filled.replace(/{{email}}/g, user.email || "");
    filled = filled.replace(/{{phone}}/g, user.mobileNumber || "");
    filled = filled.replace(/{{website}}/g, companyData?.companyUrl || "");

    return filled;
  };

  const deleteNote = async (cardId, noteId) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;
    setDeletingNoteId(noteId);
    try {
      const res = await axios.post(`${API_BASE}/delete-card-note`, { note_id: noteId }, { withCredentials: true });
      if (res.data.success) {
        setCards(cards.map(c => c.id === cardId ? { ...c, notes: c.notes?.filter(n => n.id !== noteId) || [] } : c));
        setFilteredCards(filteredCards.map(c => c.id === cardId ? { ...c, notes: c.notes?.filter(n => n.id !== noteId) || [] } : c));
      } else {
        alert(res.data.message || "Failed to delete note");
      }
    } catch (err) {
      console.error(err);
      alert("Error deleting note");
    } finally {
      setDeletingNoteId(null);
    }
  };

  const toggleSelectAll = () => {
    setSelectAll((all) => {
      const val = !all;
      setSelectedCardIndices(val ? filteredCards.map((_, idx) => idx) : []);
      return val;
    });
  };

  const handleCheckboxChange = (index) => {
    setSelectedCardIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const downloadVCF = () => {
    if (!selectedCardIndices.length) return alert("Select at least one card.");
    const vcfData = selectedCardIndices
      .map((i) => {
        const card = filteredCards[i];
        const phones = Array.isArray(card.phone) ? card.phone : [card.phone];
        return [
          "BEGIN:VCARD",
          "VERSION:3.0",
          `FN:${card.name || "Unnamed"}`,
          card.title ? `TITLE:${card.title}` : "",
          card.company ? `ORG:${card.company}` : "",
          ...phones.filter(Boolean).map((p) => `TEL;TYPE=WORK,VOICE:${p}`),
          card.email ? `EMAIL;TYPE=INTERNET:${card.email}` : "",
          card.website ? `URL:${card.website}` : "",
          card.address
            ? `ADR;TYPE=WORK:;;${card.address.replace(/\n/g, " ")}`
            : "",
          "END:VCARD",
        ]
          .filter(Boolean)
          .join("\n");
      })
      .join("\n");
    const blob = new Blob([vcfData], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts.vcf";
    a.click();
  };

  const downloadCSV = () => {
    if (!selectedCardIndices.length) return alert("Select at least one card.");
    const headers = [
      "Name",
      "Title",
      "Company",
      "Phone",
      "Email",
      "Website",
      "Address",
    ];
    const csvContent = [
      headers.join(","),
      ...selectedCardIndices.map((i) => {
        const card = filteredCards[i];
        return [
          card.name,
          card.title,
          card.company,
          card.phone,
          card.email,
          card.website,
          card.address,
        ]
          .map((field) => `"${String(field || "").replace(/"/g, '""')}"`)
          .join(",");
      }),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "contacts.csv";
    a.click();
  };

  const getCardGradient = (index) => {
    const gradients = [
      "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
    ];
    return gradients[index % gradients.length];
  };

  if (loading)
    return (
      <>
        <ResponsiveLayout currentUser={currentUser} onLogout={onLogout} />
        <Box
          sx={{
            height: "100vh",
            backgroundColor: "#FAFAFA",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Card
            elevation={4}
            sx={{ p: 3, borderRadius: 3, textAlign: "center" }}
          >
            <CircularProgress size={36} sx={{ color: "#6366F1", mb: 2 }} />
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Loading Your Cards...
            </Typography>
          </Card>
        </Box>
      </>
    );

  return (
    <>
      <ResponsiveLayout currentUser={currentUser} onLogout={onLogout} />
      <Box
        sx={{ minHeight: "100vh", backgroundColor: "#FAFAFA", pt: 10, pb: 4 }}
      >
        <Container maxWidth="lg">
          <Grow in timeout={800}>
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    variant="h3"
                    sx={{ fontWeight: "bold", color: "#1F2937", mr: 2 }}
                  >
                    Card History
                  </Typography>
                  <span
                    role="img"
                    aria-label="clipboard"
                    style={{ fontSize: 32 }}
                  >
                    📋
                  </span>
                </Box>

                {/* MODIFIED: Wrapped buttons in a Stack for spacing */}
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    startIcon={<TextSnippetIcon />}
                    onClick={() => setIsTextModalOpen(true)}
                    sx={{
                      borderRadius: 2,
                      fontWeight: "bold",
                    }}
                  >
                    Create Card With Text
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<AddCircleOutline />}
                    onClick={() => setIsManualModalOpen(true)}
                    sx={{
                      backgroundColor: "#10B981",
                      borderRadius: 2,
                      fontWeight: "bold",
                      "&:hover": { backgroundColor: "#059669" },
                    }}
                  >
                    Add Manually
                  </Button>
                </Stack>
              </Box>
              <Typography
                variant="subtitle1"
                sx={{ color: "#6B7280", textAlign: "left", mb: 4 }}
              >
                your digital business card collection
              </Typography>

              {error ? (
                <Fade in timeout={600}>
                  <Alert severity="error" sx={{ borderRadius: 3, mb: 3 }}>
                    ⚠️ {error}
                  </Alert>
                </Fade>
              ) : filteredCards.length === 0 && !loading ? (
                <Fade in timeout={600}>
                  <Card
                    elevation={4}
                    sx={{ p: 3, borderRadius: 3, textAlign: "center" }}
                  >
                    <Typography variant="h5" sx={{ mb: 2 }}>
                      📋 No Cards Found
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setSearchQuery("")}
                      startIcon={<SearchIcon />}
                    >
                      Clear Search
                    </Button>
                  </Card>
                </Fade>
              ) : (
                <>
                  <Fade in timeout={1000}>
                    <Card elevation={2} sx={{ p: 2, borderRadius: 3, mb: 2 }}>
                      <TextField
                        fullWidth
                        placeholder="Search cards..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon sx={{ color: "#6366F1" }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ mb: 2 }}
                      />

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Chip
                          icon={<CreditCardIcon />}
                          label={`${filteredCards.length} cards`}
                          color="primary"
                          variant="outlined"
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectAll}
                              onChange={toggleSelectAll}
                            />
                          }
                          label="Select All"
                        />
                      </Box>

                      <Stack direction="row" spacing={2}>
                        <Button
                          variant="contained"
                          startIcon={<FileDownloadIcon />}
                          onClick={downloadVCF}
                          disabled={selectedCardIndices.length === 0}
                        >
                          VCF
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<FileDownloadIcon />}
                          onClick={downloadCSV}
                          disabled={selectedCardIndices.length === 0}
                        >
                          CSV
                        </Button>
                        <Button
                          variant="contained"
                          startIcon={
                            isSyncing ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <CloudSync />
                            )
                          }
                          onClick={syncToLeadSeed}
                          disabled={
                            selectedCardIndices.length === 0 || isSyncing
                          }
                          sx={{
                            backgroundColor: "#4F46E5",
                            "&:hover": { backgroundColor: "#4338CA" },
                            fontWeight: "bold",
                          }}
                        >
                          {isSyncing ? "Syncing..." : "Sync to LeadSeed"}
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            handleDeleteSelected();
                          }}
                          disabled={
                            selectedCardIndices.length === 0 || isSyncing || isDeleting
                          }
                        >
                          {isDeleting ? "Deleting..." : "Delete Selected"}
                        </Button>
                      </Stack>
                    </Card>
                  </Fade>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                      gap: 3,
                    }}
                  >
                    {filteredCards.map((card, index) => (
                      <Card
                        key={card.id || index}
                        elevation={3}
                        sx={{
                          borderRadius: 3,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <Box
                          sx={{
                            background: getCardGradient(index),
                            p: 2,
                            color: "#fff",
                            position: "relative",
                          }}
                        >
                          <Box sx={{ position: "absolute", top: 7, right: 7 }}>
                            <Checkbox
                              checked={selectedCardIndices.includes(index)}
                              onChange={() => handleCheckboxChange(index)}
                              sx={{
                                color: "rgba(255,255,255,0.7)",
                                "&.Mui-checked": { color: "#fff" },
                              }}
                            />
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Avatar
                              sx={{
                                bgcolor: "rgba(255,255,255,0.2)",
                                mr: 1.5,
                              }}
                            >
                              {card.name?.charAt(0) || "?"}
                            </Avatar>
                            <Box sx={{ overflow: "hidden" }}>
                              <Typography
                                variant="subtitle1"
                                noWrap
                                sx={{ fontWeight: 700 }}
                              >
                                {card.name || "N/A"}
                              </Typography>
                              <Typography variant="body2" noWrap>
                                {card.company}
                              </Typography>
                            </Box>
                          </Box>
                          {card.scanner_username && (
                            <Chip
                              label={`Scanned by: ${card.scanner_username}`}
                              size="small"
                              sx={{
                                position: "absolute",
                                bottom: 4,
                                right: 8,
                                backgroundColor: "rgba(0,0,0,0.2)",
                                color: "white",
                                height: "20px",
                                fontSize: "0.7rem",
                              }}
                            />
                          )}
                        </Box>

                        <CardContent sx={{ p: 2 }}>
                          <Stack spacing={1}>
                            {card.phone && card.phone.split(',').map((p, idx) => (
                              <Box key={`phone-${idx}`} sx={{ display: "flex", mt: idx > 0 ? 0.5 : 0 }}>
                                <PhoneIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: "#10B981", visibility: idx > 0 ? 'hidden' : 'visible' }}
                                />
                                <Typography variant="body2">
                                  {p.trim()}
                                </Typography>
                              </Box>
                            ))}
                            {card.email && card.email.split(',').map((e, idx) => (
                              <Box key={`email-${idx}`} sx={{ display: "flex", mt: idx > 0 ? 0.5 : 0 }}>
                                <MailOutlineIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: "#6366F1", visibility: idx > 0 ? 'hidden' : 'visible' }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ wordBreak: "break-all" }}
                                >
                                  {e.trim()}
                                </Typography>
                              </Box>
                            ))}
                            {card.website && (
                              <Box sx={{ display: "flex" }}>
                                <LanguageIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: "#06B6D4" }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ wordBreak: "break-all" }}
                                >
                                  {card.website}
                                </Typography>
                              </Box>
                            )}
                            {card.address && (
                              <Box sx={{ display: "flex" }}>
                                <LocationOnIcon
                                  fontSize="small"
                                  sx={{ mr: 1, color: "#F59E0B" }}
                                />
                                <Typography variant="body2">
                                  {card.address}
                                </Typography>
                              </Box>
                            )}

                            <Divider sx={{ my: 1 }} />

                            <Stack spacing={1}>
                              {card.mailto && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleOpenSendModal(card, "email")}
                                  startIcon={<MailOutlineIcon />}
                                >
                                  Mail
                                </Button>
                              )}
                              {card.whatsapp && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  onClick={() => handleOpenSendModal(card, "whatsapp")}
                                  startIcon={<WhatsAppIcon />}
                                >
                                  WhatsApp
                                </Button>
                              )}
                              {currentUser?.is_super_admin === 1 &&
                                card.email &&
                                card.body && (
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => sendEmailToContact(card)}
                                    disabled={sendingEmailTo === card.email}
                                  >
                                    {sendingEmailTo === card.email ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      "Send Email"
                                    )}
                                  </Button>
                                )}
                            </Stack>

                            <Divider sx={{ my: 2 }} />

                            {/* NOTES SECTION */}
                            <Box sx={{ backgroundColor: "#f9fafb", p: 1.5, borderRadius: 2, border: "1px solid #e5e7eb" }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Box
                                  onClick={() => toggleNotes(card.id)}
                                  sx={{ display: "flex", alignItems: "center", cursor: "pointer", userSelect: "none" }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#4B5563", mr: 0.5 }}>
                                    Notes ({card.notes?.length || 0})
                                  </Typography>
                                  {expandedNotes[card.id] ? <ExpandLessIcon fontSize="small" sx={{ color: "#6B7280" }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: "#6B7280" }} />}
                                </Box>
                                <Button size="small" startIcon={<AddCircleOutline sx={{ fontSize: 16 }} />} onClick={() => handleAddNote(card.id)} sx={{ minWidth: "auto", textTransform: "none", p: "2px 8px" }}>
                                  Add Note
                                </Button>
                              </Box>

                              <Collapse in={expandedNotes[card.id]} timeout="auto" unmountOnExit>
                                <Box sx={{ mt: 1.5 }}>
                                  {/* LIST NOTES */}
                                  <Stack spacing={1} sx={{ mb: editingNoteId === `add_${card.id}` ? 2 : 0 }}>
                                    {(card.notes || []).map((note) => (
                                      <Box key={note.id} sx={{ backgroundColor: "white", p: 1, borderRadius: 1.5, border: "1px solid #f3f4f6" }}>
                                        {editingNoteId === note.id ? (
                                          <Box>
                                            <TextField
                                              fullWidth
                                              multiline
                                              minRows={2}
                                              maxRows={5}
                                              value={noteInputs[note.id] ?? ""}
                                              onChange={(e) => setNoteInputs({ ...noteInputs, [note.id]: e.target.value })}
                                              placeholder="Edit note..."
                                              variant="outlined"
                                              size="small"
                                              sx={{ backgroundColor: "white", "& .MuiOutlinedInput-root": { borderRadius: 1.5 }, mb: 1 }}
                                            />
                                            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                                              <Button size="small" onClick={cancelEditNote} sx={{ color: "text.secondary", minWidth: "auto", p: "2px 8px" }}>
                                                Cancel
                                              </Button>
                                              <Button
                                                size="small"
                                                variant="contained"
                                                onClick={() => saveNote(card.id, note.id)}
                                                disabled={savingNoteId === note.id}
                                                startIcon={savingNoteId === note.id ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                                sx={{ minWidth: "auto", textTransform: "none", p: "2px 8px", boxShadow: "none" }}
                                              >
                                                Save
                                              </Button>
                                            </Box>
                                          </Box>
                                        ) : (
                                          <Box>
                                            <Typography variant="body2" sx={{ color: "text.primary", whiteSpace: "pre-wrap", fontSize: "0.85rem", mb: 0.5 }}>
                                              {note.note_text}
                                            </Typography>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                              <Typography variant="caption" sx={{ color: "text.disabled", fontSize: "0.7rem" }}>
                                                {note.updated_at ? new Date(note.updated_at).toLocaleString() : ""}
                                              </Typography>
                                              <Box>
                                                <Button size="small" onClick={() => handleEditNote(note)} sx={{ minWidth: "auto", p: "2px", "& .MuiButton-startIcon": { margin: 0 } }}>
                                                  <EditIcon sx={{ fontSize: 14, color: "text.secondary" }} />
                                                </Button>
                                                <Button size="small" onClick={() => deleteNote(card.id, note.id)} disabled={deletingNoteId === note.id} shadow="none" sx={{ minWidth: "auto", p: "2px", "& .MuiButton-startIcon": { margin: 0 } }}>
                                                  {deletingNoteId === note.id ? <CircularProgress size={12} color="error" /> : <DeleteIcon sx={{ fontSize: 14, color: "error.main" }} />}
                                                </Button>
                                              </Box>
                                            </Box>
                                          </Box>
                                        )}
                                      </Box>
                                    ))}
                                  </Stack>

                                  {/* ADD NOTE SECTION */}
                                  {editingNoteId === `add_${card.id}` && (
                                    <Box sx={{ mt: 1 }}>
                                      <TextField
                                        fullWidth
                                        multiline
                                        minRows={2}
                                        maxRows={5}
                                        value={noteInputs[`add_${card.id}`] ?? ""}
                                        onChange={(e) => setNoteInputs({ ...noteInputs, [`add_${card.id}`]: e.target.value })}
                                        placeholder="Type your new note here..."
                                        variant="outlined"
                                        size="small"
                                        sx={{ backgroundColor: "white", "& .MuiOutlinedInput-root": { borderRadius: 1.5 }, mb: 1 }}
                                      />
                                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                                        <Button size="small" onClick={cancelEditNote} sx={{ color: "text.secondary", mr: 1, minWidth: "auto", p: "2px 8px" }}>
                                          Cancel
                                        </Button>
                                        <Button
                                          size="small"
                                          variant="contained"
                                          onClick={() => saveNote(card.id)}
                                          disabled={savingNoteId === `add_${card.id}`}
                                          startIcon={savingNoteId === `add_${card.id}` ? <CircularProgress size={12} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                                          sx={{ minWidth: "auto", textTransform: "none", p: "2px 8px", boxShadow: "none" }}
                                        >
                                          Save New
                                        </Button>
                                      </Box>
                                    </Box>
                                  )}
                                  {!(card.notes && card.notes.length > 0) && editingNoteId !== `add_${card.id}` && (
                                    <Typography variant="caption" sx={{ color: "text.disabled", display: "block", textAlign: "center", mt: 1 }}>
                                      No notes added yet.
                                    </Typography>
                                  )}
                                </Box>
                              </Collapse>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </>
              )}

              <Box sx={{ mt: 6, textAlign: "center" }}>
                <Button
                  variant="contained"
                  onClick={() => navigate("/scanner")}
                  startIcon={<ArrowBackIcon />}
                  size="large"
                  sx={{ px: 4, borderRadius: 3, backgroundColor: "#6366F1" }}
                >
                  Back to Scanner
                </Button>
              </Box>

              <Dialog open={sendModalOpen} onClose={() => setSendModalOpen(false)} fullWidth maxWidth="xs" sx={{ "& .MuiDialog-paper": { borderRadius: 4 } }}>
                <DialogTitle sx={{ fontWeight: 800, color: "#1e293b" }}>
                  Choose Send Method
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    How would you like to send this {sendType === "email" ? "Email" : "WhatsApp"} to {selectedCardForSend?.name}?
                  </Typography>

                  <RadioGroup value={sendMethod} onChange={(e) => setSendMethod(e.target.value)}>
                    <FormControlLabel
                      value="dynamic"
                      control={<Radio sx={{ "&.Mui-checked": { color: "#6366F1" } }} />}
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Dynamic AI Message</Typography>
                          <Typography variant="caption" color="text.secondary">Use the message generated specifically for this card.</Typography>
                        </Box>
                      }
                      sx={{ mb: 2, alignItems: "flex-start", mt: 1 }}
                    />
                    <FormControlLabel
                      value="template"
                      control={<Radio sx={{ "&.Mui-checked": { color: "#6366F1" } }} />}
                      label={
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 600 }}>Use from Template</Typography>
                          <Typography variant="caption" color="text.secondary">Select one of your pre-defined message templates.</Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start" }}
                    />
                  </RadioGroup>

                  {sendMethod === "template" && (
                    <Box sx={{ mt: 3 }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="template-select-label">Select Template</InputLabel>
                        <Select
                          labelId="template-select-label"
                          value={selectedTemplateId}
                          label="Select Template"
                          onChange={(e) => setSelectedTemplateId(e.target.value)}
                          disabled={isFetchingTemplates}
                        >
                          {templates.length === 0 ? (
                            <MenuItem disabled>No templates found</MenuItem>
                          ) : (
                            templates.map((t) => (
                              <MenuItem key={t.id} value={t.id}>
                                {t.title}
                              </MenuItem>
                            ))
                          )}
                        </Select>
                      </FormControl>
                      {isFetchingTemplates && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 1 }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </Box>
                  )}
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                  <Button onClick={() => setSendModalOpen(false)} sx={{ color: "#64748b", fontWeight: 600 }}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleConfirmSend}
                    disabled={sendMethod === "template" && !selectedTemplateId}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 700,
                      px: 3,
                      backgroundColor: "#6366F1",
                      "&:hover": { backgroundColor: "#4F46E5" },
                    }}
                  >
                    Confirm & Send
                  </Button>
                </DialogActions>
              </Dialog>

              <ManualCardModal
                open={isManualModalOpen}
                onClose={() => setIsManualModalOpen(false)}
                onSaveSuccess={() => fetchCards(true)}
                API_BASE={API_BASE}
              />
              <TextToCardModal
                open={isTextModalOpen}
                onClose={() => setIsTextModalOpen(false)}
                onSaveSuccess={() => fetchCards(true)}
                API_BASE={API_BASE}
              />
            </Box>
          </Grow>
        </Container>
      </Box>
    </>
  );
};

export default CardHistoryPage;